/**
 * Initializes WPD data points repainter.
 */
function init(wpd, dataEntryVM) {
	wpd.DataPointsRepainter = class {
		constructor(axis, dataset) {
			this.axis = axis;
			this.dataset = dataset;
			this.ctx = wpd.graphicsWidget.getAllContexts().dataCtx;
			this.painterName = "dataPointsRepainter";
		}

		drawHeaderDividers() {
			const dividers = this.axis.getMetadata()?.barHeaders?.headerDividers;
			if (!dividers) return;

			wpd.barChartUtils.drawHeaderDividers(this.ctx, dividers);
		}

		drawAxisLine() {
			const metadata = this.axis.getMetadata();
			if (!metadata.barHeaders) return;

			wpd.barChartUtils.drawAxisLine(this.ctx, this.axis, metadata);
		}

		drawLine(start, end, color) {
			wpd.barChartUtils.drawLine(this.ctx, start, end, color);
		}

		#drawPointV2() {
			if (this.axis == null) {
				// this can happen when removing widgets when a new file is loaded
				return;
			}

			const mkeys = this.dataset.getMetadataKeys();
			let hasLabels = false;
			if (
				this.axis.dataPointsHaveLabels &&
				mkeys != null &&
				mkeys[0] === "label"
			) {
				hasLabels = true;
			}

			const pointGroupColors = wpd.pointGroups.calculatePointGroupColors(
				this.axis,
			);
			const xPointGroupIndexColorMap = pointGroupColors.x;
			const yPointGroupIndexColorMap = pointGroupColors.y;

			const activePointGroup = dataEntryVM.readOnly
				? wpd.tree.getActivePointGroup()
				: null;

			this.dataset.getAllPixels().forEach((dataPoint, index) => {
				if (activePointGroup && this.dataset.hasPointGroups()) {
					const axisDir = dataPoint.metadata.pointGroupAxisDirection;
					const tupleData = dataPoint.metadata.tuple;
					const isCentralTendencyPoint =
						axisDir === "center" || !tupleData?.[axisDir];

					const matchesActiveFilter = activePointGroup.isCentralTendency
						? isCentralTendencyPoint
						: !isCentralTendencyPoint &&
						  axisDir === activePointGroup.axisDirection &&
						  tupleData[axisDir].tagUuid === activePointGroup.tag.uuid;

					if (!matchesActiveFilter) return;
				}

				const isSelected = this.dataset.getSelectedPixels().indexOf(index) >= 0;

				let fillStyle = isSelected ? wpd.Colors.active : wpd.Colors.good;

				if (hasLabels) {
					let pointLabel = null;
					if (this.dataset.hasPointGroups()) {
						const tupleData = dataPoint.metadata.tuple;

						if (tupleData) {
							pointLabel = dataPoint.metadata?.label?.value;
							if (pointLabel === "") {
								pointLabel =
									this.axis.dataPointsLabelPrefix +
									(tupleData.index > -1 ? tupleData.index : index);
							}
						}
					} else {
						pointLabel = dataPoint.metadata?.label?.value;
						if (pointLabel === "") {
							pointLabel = this.axis.dataPointsLabelPrefix + index;
						}
					}
					wpd.graphicsHelper.drawPoint(dataPoint, fillStyle, pointLabel);
				} else {
					if (dataEntryVM.pointGroupColors && this.dataset.hasPointGroups()) {
						const tupleData = dataPoint.metadata.tuple;

						const axisDirection = dataPoint.metadata.pointGroupAxisDirection;

						const groupIndex = this.dataset
							.getMetadata()
							.pointGroups.findIndex(
								(pointGroup) =>
									pointGroup.axis === axisDirection &&
									pointGroup.tagUuid === tupleData[axisDirection].tagUuid,
							);

						if (axisDirection && !isSelected) {
							if (
								axisDirection === "y" &&
								yPointGroupIndexColorMap.has(groupIndex)
							) {
								fillStyle = yPointGroupIndexColorMap.get(groupIndex);
							} else if (
								axisDirection === "x" &&
								xPointGroupIndexColorMap.has(groupIndex)
							) {
								fillStyle = xPointGroupIndexColorMap.get(groupIndex);
							}
						}
					}

					wpd.graphicsHelper.drawPoint(dataPoint, fillStyle);
				}
			});
		}

		drawPoints() {
			if (dataEntryVM.pointGroupOverhaul) {
				this.#drawPointV2();
			} else {
				let mkeys = this.dataset.getMetadataKeys();
				let hasLabels = false;

				if (this.axis == null) {
					return; // this can happen when removing widgets when a new file is loaded:
				}

				if (
					this.axis.dataPointsHaveLabels &&
					mkeys != null &&
					mkeys[0] === "label"
				) {
					hasLabels = true;
				}

				const pointGroupColors = wpd.pointGroups.calculatePointGroupColors(
					this.axis,
				);
				const xPointGroupIndexColorMap = pointGroupColors.x;
				const yPointGroupIndexColorMap = pointGroupColors.y;

				for (let dindex = 0; dindex < this.dataset.getCount(); dindex++) {
					let imagePos = this.dataset.getPixel(dindex);
					let isSelected =
						this.dataset.getSelectedPixels().indexOf(dindex) >= 0;

					let fillStyle = isSelected ? wpd.Colors.active : wpd.Colors.good;

					if (hasLabels) {
						let pointLabel = null;
						if (this.dataset.hasPointGroups()) {
							// with point groups, bar labels only apply to points in the primary group (i.e. index 0)
							const tupleIndex = this.dataset.getTupleIndex(dindex);
							const groupIndex = this.dataset.getPointGroupIndexInTuple(
								tupleIndex,
								dindex,
							);

							if (groupIndex <= 0) {
								pointLabel = imagePos.metadata?.label?.value;
								const index = tupleIndex > -1 ? tupleIndex : dindex;
								if (pointLabel === "") {
									pointLabel = this.axis.dataPointsLabelPrefix + index;
								}
							}
						} else {
							pointLabel = imagePos.metadata?.label?.value;
							if (pointLabel === "") {
								pointLabel = this.axis.dataPointsLabelPrefix + dindex;
							}
						}
						wpd.graphicsHelper.drawPoint(imagePos, fillStyle, pointLabel);
					} else {
						if (dataEntryVM.pointGroupColors && this.dataset.hasPointGroups()) {
							// with point groups, bar labels only apply to points in the primary group (i.e. index 0)
							const tupleIndex = this.dataset.getTupleIndex(dindex);
							const groupIndex = this.dataset.getPointGroupIndexInTuple(
								tupleIndex,
								dindex,
							);

							const axisDirection =
								imagePos.metadata?.tagGroupAxisDirection ??
								Object.keys(imagePos.metadata)
									.filter((key) => !isNaN(key))
									.map((key) => {
										const px = imagePos.metadata[key];
										return wpd.pointGroups.findAxisDirectionForTag(
											this.axis,
											px.tag,
										);
									})
									.find((direction) => direction != null);

							if (axisDirection && !isSelected) {
								if (
									axisDirection === "y" &&
									yPointGroupIndexColorMap.has(groupIndex)
								) {
									fillStyle = yPointGroupIndexColorMap.get(groupIndex);
								} else if (
									axisDirection === "x" &&
									xPointGroupIndexColorMap.has(groupIndex)
								) {
									fillStyle = xPointGroupIndexColorMap.get(groupIndex);
								}
							}
						}

						wpd.graphicsHelper.drawPoint(imagePos, fillStyle);
					}
				}
			}
		}

		onAttach() {
			wpd.graphicsWidget.resetData();
			if (!dataEntryVM.pointGroupOverhaul) {
				this.drawPoints();
				this.drawAxisLine();
				this.drawHeaderDividers();
			}
		}

		onRedraw() {
			this.drawPoints();
			this.drawAxisLine();
			this.drawHeaderDividers();
		}

		onForcedRedraw() {
			wpd.graphicsWidget.resetData();
			if (!dataEntryVM.pointGroupOverhaul) {
				this.drawPoints();
				this.drawAxisLine();
				this.drawHeaderDividers();
			}
		}
	};
}

export { init };
